import type { IAnalyticsPropContext } from '@inter-ikea-kompis/analytics';
import { AbstractAnalyticsTracker } from '@inter-ikea-kompis/analytics';
import type { IDexfSettings } from '@inter-ikea-kompis/types';

import { IpexMomentEnum } from '@insights/insights-data-provider';

import ShareDesignCardStateEnum from '../enums/ShareDesignCardStateEnum.js';

const moduleId = 'share_design_card';

// Converts selected state in browser event 'select' to the corresponding name it should have in the analytics event 'share_design_select_option'
const BrowserEventSelectedStateToAnalyticsEventSelectedOption = {
	[ShareDesignCardStateEnum.sendByEmail]: 'email',
	[ShareDesignCardStateEnum.getDesignCode]: 'code',
	[ShareDesignCardStateEnum.getLink]: 'link'
};

/**
 * Tracks events from Share Design Card and sends them to a reporter.
 *
 * @event share_design_select_option When a share option (item in action list) is clicked.
 * @event share_design_send_by_email When send button for sending email is clicked.
 * @event share_design_copy_code When copy code button is clicked.
 * @event share_design_copy_link When copy link button is clicked.
 * @event share_design_initial_configuration When the component is rendered the first time.
 */
export default class ShareDesignCardTracker extends AbstractAnalyticsTracker {
	private readonly context: IAnalyticsPropContext['context'];

	/**
	 * @param options ...
	 * @param options.dev ...
	 * @param options.ab ...
	 * @param options.settings ...
	 * @param options.locale ...
	 * @param options.reporterId ...
	 * @param options.context The context where the {@link SaveDesignCard} component is rendered, e.g. 'main_page', or 'summary_page'. This is used to distinguish the analytics data if the component is used in several places in a planner.
	 */
	constructor(options: {
		dev: boolean;
		ab?: boolean;
		settings: IDexfSettings;
		locale: string;
		reporterId?: string;
		context?: IAnalyticsPropContext['context'];
	}) {
		super({ moduleId, ...options });
		this.context = options.context;
	}

	/**
	 * Call to report the initial config of ShareDesignCard.
	 *
	 * @param payload ...
	 * @param payload.showEmailOption ...
	 * @param payload.showCopyLinkOption ...
	 * @param payload.showCopyCodeButton ...
	 * @param payload.showQrCodeOption ...
	 */
	public sendInitialConfiguration(payload: {
		showEmailOption: boolean;
		showCopyLinkOption: boolean;
		showCopyCodeButton: boolean;
		showQrCodeOption: boolean;
	}): void {
		this.getReporter().report({
			event: 'share_design_initial_configuration',
			ipexMoment: IpexMomentEnum.nonInteraction,
			payload: {
				...payload,
				context: this.context
			}
		});
	}

	/**
	 * Call to report when a share option (item in action list) is clicked.
	 *
	 * @param selectedState The selected state in Share Design Card.
	 */
	public sendSelectOption(
		selectedState: Exclude<ShareDesignCardStateEnum, ShareDesignCardStateEnum.default>
	): void {
		this.getReporter().report({
			event: 'share_design_select_option',
			ipexMoment: IpexMomentEnum.giveMe,
			payload: {
				option: BrowserEventSelectedStateToAnalyticsEventSelectedOption[selectedState],
				context: this.context
			}
		});
	}

	/**
	 * Call to report when send button for sending email is clicked.
	 *
	 * @param designId The design id (VPC code).
	 */
	public sendSendByEmail(designId: string): void {
		this.getReporter().report({
			event: 'share_design_send_by_email',
			ipexMoment: IpexMomentEnum.giveMe,
			payload: { designId, context: this.context }
		});
	}

	/**
	 * Call to report when copy code button is clicked.
	 *
	 * @param designId The design id (VPC code).
	 */
	public sendCopyCode(designId: string): void {
		this.getReporter().report({
			event: 'share_design_copy_code',
			ipexMoment: IpexMomentEnum.giveMe,
			payload: { designId, context: this.context }
		});
	}

	/**
	 * Call to report when copy link button is clicked.
	 *
	 * @param designId The design id (VPC code).
	 */
	public sendCopyLink(designId: string): void {
		this.getReporter().report({
			event: 'share_design_copy_link',
			ipexMoment: IpexMomentEnum.giveMe,
			payload: { designId, context: this.context }
		});
	}
}
